import { CommonModule } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import { SchedulesHelperService } from '../../../pages/schedules/schedules-helper.service';
import { SchedulesApiService } from '../../../pages/schedules/schedules-api.service';
import { CourseApiServiceService } from '../../../pages/courses/course-api-service.service';
import { finalize } from 'rxjs';

@Component({
  selector: 'app-schedule-view',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './schedule-view.component.html',
  styleUrl: './schedule-view.component.css'
})
export class ScheduleViewComponent implements OnInit {

  eventId: any;
  selectedEvent: any;
  eventData: any = null;
  isLoading: boolean = false;

  constructor(
    private helperService: SchedulesHelperService,
    private schedulesApiService: SchedulesApiService,
    private courseApiService: CourseApiServiceService
  ) {
    this.eventId = this.helperService.eventId();
    this.selectedEvent = this.helperService.selectedEvent();
  }

  ngOnInit() {
    // Use selected event data if available, otherwise fetch by ID
    if (this.eventId) {
      const rawId = this.normalizeEventId(this.eventId);
      if (!rawId) return;
      this.isLoading = true;
      this.schedulesApiService.getEventById(rawId).subscribe({
        next: (res: any) => {
          const payload =
            res?.status === 'success' ? res?.data : res?.data ?? res;
          this.eventData = this.transformEventData(payload ?? this.selectedEvent);
          this.resolveCourseNameIfNeeded();
        },
        error: (err: any) => {
          console.error('Failed to fetch event details:', err);
          if (this.selectedEvent) {
            this.eventData = this.transformEventData(this.selectedEvent);
          }
        },
        complete: () => {
          this.isLoading = false;
        },
      });
      return;
    }
    if (this.selectedEvent) {
      this.eventData = this.transformEventData(this.selectedEvent);
      this.resolveCourseNameIfNeeded();
    }
  }


  private resolveCourseNameIfNeeded() {
    if (!this.eventData || this.eventData.course_name || !this.eventData.course_id) {
      return;
    }

    const courseId = Number(this.eventData.course_id);
    if (!courseId) {
      return;
    }

    this.courseApiService.getCourseById(courseId).subscribe({
      next: (res: any) => {
        const course = res?.data ?? res;
        const courseName =
          course?.course_name ?? course?.name ?? course?.title ?? null;
        if (courseName) {
          this.eventData.course_name = courseName;
          if (!this.eventData.title || this.eventData.title === '') {
            this.eventData.title = courseName;
          }
        }
      },
      error: () => {
        // Ignore course lookup failures; keep existing display values.
      },
    });
  }

  private transformEventData(apiEvent: any): any {
    // Transform API response to match component's expected structure
    const startDate = new Date(apiEvent.start);
    const endDate = new Date(apiEvent.end);

    return {
      id: apiEvent.schedule_day_id || apiEvent.classroom_schedule_id || apiEvent.id,
      type: apiEvent.source === 'schedule' ? 'course' : 'event',
      eventDate: [startDate.toISOString().split('T')[0]], // Format as YYYY-MM-DD
      eventTime: [
        startDate.toTimeString().slice(0, 5), // HH:MM format
        endDate.toTimeString().slice(0, 5)
      ],
      title: apiEvent.course_title ?? apiEvent.session_title ?? apiEvent.summary ?? apiEvent.title ?? '',
      course_name: apiEvent.course_title ?? apiEvent.session_title ?? null,
      description: apiEvent.description || apiEvent.details || '',
      location: apiEvent.location || '',
      link: apiEvent.meet_link || apiEvent.link || '',
      parameters: apiEvent.additional_parameter || [],
      teacher: apiEvent.attendee || [], // Assuming attendee contains teacher info
      classes: apiEvent.class_id?.map((cls: any) => cls.class_name) || [],
      // Additional fields from API response
      source: apiEvent.source,
      event_id: apiEvent.event_id,
      schedule_day_id: apiEvent.schedule_day_id,
      classroom_schedule_id: apiEvent.classroom_schedule_id,
      status: apiEvent.status,
      session_type: apiEvent.session_type,
      session_title: apiEvent.session_title,
      course_id: apiEvent.course_id,
      class_details: apiEvent.class_id || [],
      batch_details: apiEvent.batch_id || [],
      group_details: apiEvent.group_id || [],
      department_details: apiEvent.department_id || [],
      attendee_details: apiEvent.attendee || []
    };
  }

  private setDefaultEventData() {
    this.eventData = {
      id: 1,
      type: 'course',
      eventDate: ['2023-01-01'],
      eventTime: ['10:00', '11:00'],
      title: 'Frontend Frameworks Seminar',
      description: 'Join us for a virtual introduction to Angular, a powerful framework for building dynamic and interactive web applications.',
      location: 'Online',
      link: 'https://meet.google.com/abc',
      parameters: ['Revision', 'Guest Lecture', 'Workshop'],
      teacher: ['John Doe', 'Jane Smith'],
      classes: ['Class 1', 'Class 2', 'Class 3', 'Class 4'],
    };
  }

  private normalizeEventId(value: any): string | number | null {
    if (value === undefined || value === null || value === '') return null;
    const rawString = String(value);
    if (rawString.startsWith('evt-')) {
      return rawString.replace(/^evt-/, '').split('-')[0];
    }
    return rawString.split('-')[0];
  }
}
